/*************************************************
 * ByzFunder
 * @exports
 * customFetchBase.ts
 * Created by vinoth kumar on  15/05/2024
 * Copyright © 2024 ByzFunder. All rights reserved.
 *************************************************/

// Import necessary modules
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import { Mutex } from "async-mutex";
import { logout } from "./../slice/UserSlice";
import store from "./../SessionStorage";
import { detectBrandSlug } from "../../branding/hostMap";

// Function to get brand-specific API base URL (called dynamically)
function getApiBaseUrl(): string {
  // Detect current brand
  const qsBrand = new URLSearchParams(window.location.search).get('brand');
  const brand = detectBrandSlug(window.location.hostname, qsBrand);
  
  // Check for brand-specific API URL environment variables
  if (brand === 'byzwash' && process.env.REACT_APP_BYZWASH_API_BASE_URL) {
    return process.env.REACT_APP_BYZWASH_API_BASE_URL;
  }
  
  if (brand === 'byzfunder' && process.env.REACT_APP_BYZFUNDER_API_BASE_URL) {
    return process.env.REACT_APP_BYZFUNDER_API_BASE_URL;
  }
  
  // Fall back to default API URL
  return process.env.REACT_APP_CUSTOMER_API_BASE_URL || '';
}

// Create a mutex to manage concurrent access
const mutex = new Mutex();

// Custom base query that uses brand-specific API URL dynamically
const createBaseQuery = () => {
  const brandApiUrl = getApiBaseUrl();
  return fetchBaseQuery({
    baseUrl: brandApiUrl,
    prepareHeaders: (headers) => {
      headers.set("Accept", "application/json");
      // Identifies this client as the web Customer Portal so the backend
      // can differentiate web vs. mobile app activity for logging (ENG-4717)
      headers.set("X-Client-Source", "web");
      const user = store.get("user")
        ? JSON.parse(store.get("user")!)
        : null;
      if (user) {
        headers.set("authorization", `Bearer ${user?.token}`);
      }
      return headers;
    },
  });
};

// Custom fetch function to intercept and handle requests
const customFetchBase = async (args: any, api: any, extraOptions: any) => {
  // Wait for mutex to unlock
  await mutex.waitForUnlock();
  
  // Create baseQuery with current brand-specific API URL
  const baseQuery = createBaseQuery();
  
  // Make the request using baseQuery
  let result = await baseQuery(args, api, extraOptions);
  // If request returns Unauthorized (401)
  if (result.error?.status === 401) {
    // If mutex is not locked, acquire it
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        // Retry request with updated token
        // result = await baseQuery(args, api, extraOptions);
        const user = store.get("user")
          ? JSON.parse(store.get("user")!)
          : null;
        if (user) {
          api.dispatch(logout({}));
          window.location.href = "/";
        }
      } finally {
        // Release mutex
        release();
      }
    } else {
      // If mutex is locked, wait for it to unlock and retry request
      await mutex.waitForUnlock();
      const retryBaseQuery = createBaseQuery();
      result = await retryBaseQuery(args, api, extraOptions);
    }
  }
  return result;
};

export default customFetchBase;